/**
 * /dev/map: the map engine on fake data that mirrors the demo stories, to judge it in the real shell
 * before the pages feed it live data. Two maps: the whole tenant as Now shows it (13 systems, 22 data
 * models, 15 integrations: the DWH split, the meter merge, the red Maventa flow, the collective
 * invoicing merge, the delivery confirmations going back) and one order's stage (6 lanes, a selection,
 * an as-of that greys what came later, an e-invoice that never arrived). A speed control for testing.
 */
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Map as MapView } from '../map/Map.tsx';
import type { MapFlow, MapLink, MapMotion, MapNode } from '../map/types.ts';
import { failedLabelOf, flowLabel, noun } from '../map/adapters.ts';
import { rhythmOf } from '../lib/rhythm.ts';
import { age } from '../lib/freshness.ts';
import { count, duration } from '../lib/format.ts';
import { Kicker, Pill, cx } from '../design/primitives.tsx';

const MIN = 60_000;
const H = 60 * MIN;
const D = 24 * H;

function node(now: number, id: string, system: string, format: string, total: number, lastAgo: number, usual: number, failed?: number): MapNode {
  const last = now - lastAgo;
  return {
    id, label: noun(id, total), system, format, count: total, countLabel: 'today', last, usual, failed,
    detail: `${count(total)} today · ${age(last, now)}`,
  };
}

/** Starts for a rhythm: on events spread over the day, daily at an hour over three days, hourly at :02. */
function starts(now: number, kind: 'events' | 'daily' | 'hourly', runs: number, hour = 18): number[] {
  if (kind === 'hourly') return Array.from({ length: runs }, (_, i) => dayjs(now).startOf('hour').valueOf() - i * H + 2 * MIN);
  if (kind === 'daily') return Array.from({ length: Math.max(3, runs) }, (_, i) => dayjs(now).startOf('day').hour(hour).minute(0).valueOf() - (i + 1) * D);
  // events: a pseudo-random spread, the same every render
  let seed = runs * 7919;
  return Array.from({ length: runs }, () => {
    seed = (seed * 48271) % 2147483647;
    return now - (seed % (20 * H));
  });
}

function flow(now: number, process: string, from: string, to: string | null, runs: number, failed: number, o: {
  rhythm?: 'events' | 'daily' | 'hourly'; hour?: number; split?: number; merge?: number; fin: string; fout?: string; external?: string; ongoingMin?: number;
}): MapFlow {
  const rhythm = rhythmOf(starts(now, o.rhythm ?? 'events', runs, o.hour));
  const system = /^\[([A-Z]+)\]/.exec(process)?.[1] ?? '';
  const ongoing = o.ongoingMin ? { since: now - o.ongoingMin * MIN, now } : null;
  return {
    id: `${process}→${to ?? 'out'}`, process, system, from, to, external: o.external ?? null, runs, failed,
    label: flowLabel(runs, failed, rhythm, ongoing), failing: ongoing && { since: ongoing.since, count: failed },
    rhythm, split: o.split, merge: o.merge, formatIn: o.fin, formatOut: o.fout ?? o.fin,
  };
}

/** The whole tenant as Now shows it, on fake data: also what an empty vault demos itself with (pages/Now.tsx). */
export function nowData(now: number) {
  const nodes: MapNode[] = [
    node(now, 'WebshopCustomer', 'WEB', 'json', 2, 2 * H, 6 * H),
    node(now, 'WebshopOrder', 'WEB', 'json', 47, 3 * MIN, 10 * MIN),
    node(now, 'WebshopFulfillment', 'WEB', 'json', 19, 36 * MIN, 30 * MIN),
    node(now, 'WebshopReturn', 'WEB', 'json', 2, 3 * H, 6 * H),
    node(now, 'EdiPurchaseOrder', 'EDI', 'xml', 6, 51 * MIN, 2 * H),
    node(now, 'EdiOrderResponse', 'EDI', 'xml', 6, 50 * MIN, 2 * H),
    node(now, 'CrmCustomer', 'CRM', 'json', 3, 20 * MIN, 4 * H),
    node(now, 'ErpCustomer', 'ERP', 'xml', 3, 20 * MIN, 4 * H, 1),
    node(now, 'ErpExchangeRate', 'ERP', 'xml', 4, 19 * H, D),
    node(now, 'ErpSalesOrder', 'ERP', 'xml', 53, 3 * MIN, 10 * MIN),
    node(now, 'ErpReturnOrder', 'ERP', 'json', 2, 3 * H, 6 * H),
    node(now, 'WmsOutboundOrder', 'WMS', 'xml', 41, 25 * MIN, 4 * MIN),
    node(now, 'WmsDelivery', 'WMS', 'xml', 28, 30 * MIN, 12 * MIN, 7),
    node(now, 'CarrierTracking', 'CARRIER', 'json', 44, 5 * MIN, 8 * MIN),
    node(now, 'FinvoiceInvoice', 'FIN', 'xml', 24, 12 * MIN, 20 * MIN, 11),
    node(now, 'DwhSalesLine', 'DWH', 'json', 142, 6 * MIN, 10 * MIN),
    node(now, 'EcbReferenceRates', 'ECB', 'json', 4, 19 * H, D),
    node(now, 'HrEmployee', 'HR', 'json', 3, 5 * H, D),
    node(now, 'PayrollEmployee', 'PAY', 'xml', 3, 5 * H, D, 1),
    node(now, 'MeterReading', 'FAC', 'json', 48, 40 * MIN, H),
    node(now, 'EnergyConsumption', 'ESG', 'json', 12, 40 * MIN, H),
  ];
  const flows: MapFlow[] = [
    flow(now, '[CRM] Customer Master Sync', 'CrmCustomer', 'ErpCustomer', 3, 1, { fin: 'json', fout: 'xml', ongoingMin: 21 }),
    flow(now, '[WEB] Webshop Orders to ERP', 'WebshopOrder', 'ErpSalesOrder', 47, 0, { fin: 'json', fout: 'xml' }),
    flow(now, '[WEB] Order Cancellations to ERP', 'WebshopOrder', 'ErpSalesOrder', 4, 0, { fin: 'json', fout: 'xml' }),
    flow(now, '[WEB] Returns to ERP', 'WebshopReturn', 'ErpReturnOrder', 2, 0, { fin: 'json' }),
    flow(now, '[EDI] Purchase Orders to ERP', 'EdiPurchaseOrder', 'ErpSalesOrder', 6, 0, { fin: 'xml' }),
    flow(now, '[EDI] Order Confirmations to Customers', 'EdiOrderResponse', null, 6, 0, { fin: 'xml', external: 'customers' }),
    flow(now, '[ERP] Sales Orders to WMS', 'ErpSalesOrder', 'WmsOutboundOrder', 41, 0, { fin: 'xml' }),
    flow(now, '[ERP] Order Lines to Data Warehouse', 'ErpSalesOrder', 'DwhSalesLine', 41, 0, { fin: 'xml', fout: 'json', split: 3 }),
    flow(now, '[ERP] Collective Invoicing', 'ErpSalesOrder', 'FinvoiceInvoice', 3, 0, { rhythm: 'daily', hour: 18, fin: 'xml', merge: 3 }),
    flow(now, '[FIN] Invoices to e-invoice operator', 'FinvoiceInvoice', null, 24, 11, { fin: 'xml', external: 'e-invoice operator', ongoingMin: 52 }),
    flow(now, '[WMS] Delivery Confirmations to ERP', 'WmsDelivery', 'ErpSalesOrder', 28, 4, { fin: 'xml' }),
    flow(now, '[CARRIER] Tracking Events to Webshop', 'CarrierTracking', 'WebshopFulfillment', 44, 0, { fin: 'json' }),
    flow(now, '[HR] Employees to Payroll', 'HrEmployee', 'PayrollEmployee', 3, 1, { rhythm: 'daily', hour: 6, fin: 'json', fout: 'xml' }),
    flow(now, '[FAC] Meter Readings to Energy Reporting', 'MeterReading', 'EnergyConsumption', 24, 0, { rhythm: 'hourly', fin: 'json', merge: 3 }),
    flow(now, '[ECB] Exchange Rates to ERP', 'EcbReferenceRates', 'ErpExchangeRate', 3, 0, { rhythm: 'daily', hour: 16, fin: 'json', fout: 'xml' }),
  ];
  // One failure, one number: a node's red line names what it counts when that is not its arrow's number
  for (const n of nodes) n.failedLabel = failedLabelOf(n.id, n.failed ?? 0, flows);
  return { nodes, flows };
}

/** One order's journey, with the real hop times instead of counts. */
function stageData(now: number) {
  const placed = dayjs(now).startOf('day').hour(9).minute(12).subtract(1, 'day').valueOf();
  const at = (minutesAfter: number) => placed + minutesAfter * MIN;
  const hops: Record<string, number> = {
    WebshopOrder: at(0), ErpSalesOrder: at(2), WmsOutboundOrder: at(28), DwhSalesLine: at(40),
    WmsDelivery: at(26 * 60 + 53), CarrierTracking: at(26 * 60 + 56), WebshopFulfillment: at(27 * 60), ErpInvoice: at(27 * 60 + 18),
  };
  const rec = (id: string, system: string, format: string, n: number): MapNode => ({
    id, label: noun(id, n), system, format, count: n, countLabel: 'records', last: hops[id], usual: null,
    detail: `${count(n)} ${n === 1 ? 'record' : 'records'} · ${age(hops[id], now)}`,
  });
  const nodes: MapNode[] = [
    rec('WebshopOrder', 'WEB', 'json', 1), rec('ErpSalesOrder', 'ERP', 'xml', 1), rec('WmsOutboundOrder', 'WMS', 'xml', 1),
    rec('DwhSalesLine', 'DWH', 'json', 3), rec('WmsDelivery', 'WMS', 'xml', 1), rec('CarrierTracking', 'CARRIER', 'json', 6),
    rec('WebshopFulfillment', 'WEB', 'json', 1), rec('ErpInvoice', 'ERP', 'xml', 1),
    {
      id: 'FinvoiceInvoice', label: noun('FinvoiceInvoice', 2), system: 'FIN', format: null, count: 0, countLabel: 'records',
      last: null, usual: null, absent: true, detail: 'never arrived',
    },
  ];
  const times = (from: string, to: string) => `${dayjs(Math.max(hops[from], hops[to])).format('ddd HH:mm')} · ${duration(Math.abs(hops[to] - hops[from]))} later`;
  const one = (process: string, from: string, to: string | null, o: { fin: string; fout?: string; split?: number; failed?: number; ongoingMin?: number }): MapFlow => {
    const f = flow(now, process, from, to, 1, o.failed ?? 0, { fin: o.fin, fout: o.fout, split: o.split, ongoingMin: o.ongoingMin });
    return { ...f, timesLabel: to && hops[to] && hops[from] ? times(from, to) : undefined };
  };
  const flows: MapFlow[] = [
    one('[WEB] Webshop Orders to ERP', 'WebshopOrder', 'ErpSalesOrder', { fin: 'json', fout: 'xml' }),
    one('[ERP] Sales Orders to WMS', 'ErpSalesOrder', 'WmsOutboundOrder', { fin: 'xml' }),
    one('[ERP] Order Lines to Data Warehouse', 'ErpSalesOrder', 'DwhSalesLine', { fin: 'xml', fout: 'json', split: 3 }),
    one('[WMS] Delivery Confirmations to ERP', 'WmsDelivery', 'ErpSalesOrder', { fin: 'xml' }),
    one('[CARRIER] Tracking Events to Webshop', 'CarrierTracking', 'WebshopFulfillment', { fin: 'json' }),
    one('[ERP] Collective Invoicing', 'ErpSalesOrder', 'ErpInvoice', { fin: 'xml' }),
    one('[FIN] Invoices to e-invoice operator', 'ErpInvoice', 'FinvoiceInvoice', { fin: 'xml', failed: 1, ongoingMin: 52 }),
  ];
  const links: MapLink[] = [{ from: 'WmsOutboundOrder', to: 'WmsDelivery', via: ['OutboundOrder/OrderNumber', 'Delivery/OrderNumber'] }];
  const offsets = new Map(Object.entries(hops).map(([id, t]) => [id, t - hops.ErpSalesOrder]));
  offsets.set('FinvoiceInvoice', hops.ErpInvoice - hops.ErpSalesOrder + 2 * MIN);
  return { nodes, flows, links, offsets, asOf: hops.WmsDelivery - 30 * MIN };
}

const SPEEDS: [string, number][] = [['½×', 0.5], ['1×', 1], ['2×', 2]];

export default function MapDemo() {
  const now = useMemo(() => Date.now(), []);
  const [speed, setSpeed] = useState(1);
  const [motion, setMotion] = useState<MapMotion>('live');
  const [selection, setSelection] = useState<string | null>('ErpSalesOrder');
  const [picked, setPicked] = useState<string>('');
  const [hovered, setHovered] = useState<string | null>(null);
  const [asOfOn, setAsOfOn] = useState(true);
  const big = useMemo(() => nowData(now), [now]);
  const scoped = useMemo(() => stageData(now), [now]);
  const stageLayout = useMemo(() => ({ offsets: scoped.offsets, laneOrder: ['WEB', 'ERP', 'WMS', 'CARRIER', 'FIN', 'DWH'] }), [scoped]);
  const nowLayout = useMemo(() => ({ maxHeight: 740 }), []);

  return (
    <div className="max-w-[1440px] mx-auto px-6 py-6 flex flex-col gap-10">
      <section className="flex flex-col gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Kicker>Now · 13 systems · 21 data models · 15 integrations</Kicker>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-small text-n8 min-w-0 truncate max-w-[520px]">{picked || hovered || ''}</span>
            <div className="flex items-center bg-n3 rounded-full p-0.5">
              <Pill active={motion === 'paused'} onClick={() => setMotion('paused')}>paused</Pill>
              {SPEEDS.map(([label, v]) => (
                <Pill key={label} active={motion === 'live' && speed === v} onClick={() => { setMotion('live'); setSpeed(v); }}>{label}</Pill>
              ))}
              <Pill active={motion === 'off'} onClick={() => setMotion('off')}>still</Pill>
            </div>
          </div>
        </div>
        <div className="rounded-stage overflow-hidden">
          <MapView nodes={big.nodes} flows={big.flows} layout={nowLayout} now={now} motion={motion} speed={speed} glyphScale={1}
            onNode={(id) => setPicked(`node ${id}`)} onFlow={(f) => setPicked(`flow ${f.process}`)}
            onGlyphHover={(f, at) => setHovered(f ? `document of ${f.process} at ${Math.round(at?.x ?? 0)},${Math.round(at?.y ?? 0)}` : null)} />
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Kicker>Stage · SO-757613 · 6 lanes · glyphs 0.8</Kicker>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-small text-n8">as of {dayjs(scoped.asOf).format('ddd HH:mm')}</span>
            <div className="flex items-center bg-n3 rounded-full p-0.5">
              <Pill active={asOfOn} onClick={() => setAsOfOn(true)}>then</Pill>
              <Pill active={!asOfOn} onClick={() => setAsOfOn(false)}>now</Pill>
            </div>
          </div>
        </div>
        <div className={cx('rounded-stage overflow-hidden')}>
          <MapView nodes={scoped.nodes} flows={scoped.flows} links={scoped.links} layout={stageLayout} now={now} motion={motion} speed={speed} glyphScale={0.8}
            selection={selection} asOf={asOfOn ? scoped.asOf : null}
            onNode={(id) => { setSelection(id); setPicked(`node ${id}`); }} onFlow={(f) => setPicked(`flow ${f.process}`)} />
        </div>
      </section>
    </div>
  );
}
